import { definedMap } from "@glideapps/ts-necessities";

import {
    ComponentKind,
    ComponentDescription,
    TitleDescription,
    InputOutputTables,
    TableGlideType,
    MutatingScreenKind,
    LegacyPropertyDescription,
    getTableColumn,
    isComputedColumn,
    getTableName,
    PropertyKind,
    PropertyDescription,
    ActionComponentDescription,
    makeEmptyComponentDescription,
} from "../description";
import { SerializedComponent } from "../../components/SerializedApp";
import {
    ComponentBuilder,
    ComponentDescriptor,
    ComponentConfiguratorContext,
    PropertyDescriptor,
    SchemaInspector,
    ActionPropertyDescriptor,
} from ".";
import { ComponentHandlerBase } from "./handler";
import { makeImageSource, makeLetsForRowIndexOrRowID, makeRowIndexOrRowID, makeText } from "../builder-utils";
import { makeSummaryImageKindPropertyDescriptor, makeTitleSubtitleImagePropertyDescriptors } from "./descriptor-utils";
import { getDocURL } from "../../docUrl";
import { SerializedElementBuilder } from "../serialized-component";
import { getColumnProperty, getEnumProperty } from "./property-utils";
import { TitleAppearance } from "./title-style";
import { PropertySection } from "./property-sections";
import { ImageAspectRatio } from "./image-types";
import { makeScreenContextForActions } from "../actions";
import { makeDefaultActionDescriptor } from "./primitive";

const ComponentKindTitle: ComponentKind = "big-item";

export interface TitleComponentDescription extends ActionComponentDescription, ComponentDescription {
    readonly titleProperty: LegacyPropertyDescription | undefined;
    readonly subtitleProperty: LegacyPropertyDescription | undefined;
    readonly imageKind: LegacyPropertyDescription | undefined;
    readonly imageURLProperty: LegacyPropertyDescription | undefined;
    readonly appearanceProperty: LegacyPropertyDescription | undefined;
    readonly aspectProperty: PropertyDescription | undefined;
}

// FIXME: These are for legacy DropImageAction invocations and not actually used here.
export function makeDropImageActionForColumnDescription(
    b: SerializedElementBuilder,
    table: TableGlideType,
    columnDescription: LegacyPropertyDescription | undefined
): SerializedComponent | undefined {
    const propertyName = getColumnProperty(columnDescription);
    if (propertyName === undefined) return undefined;

    const column = getTableColumn(table, propertyName);
    if (column === undefined || isComputedColumn(column) || column.type.kind === "array") return undefined;

    const tableName = getTableName(table);

    // We saw this once in a log on prod
    if (tableName.isSpecial) return undefined;

    const { lets, rowIndexOrRowID } = makeLetsForRowIndexOrRowID(
        b,
        makeRowIndexOrRowID(b, table, undefined, b.getContext)
    );
    return b.let(
        [["imageURL", b.getProperty(propertyName, b.getContext, table)], ...lets],
        b.dropImageAction(tableName.name, propertyName, rowIndexOrRowID, b.getVariable("imageURL"))
    );
}

export class TitleComponentHandler extends ComponentHandlerBase<TitleComponentDescription> {
    constructor() {
        super(ComponentKindTitle);
    }

    public entryForTableColumn(
        builder: ComponentBuilder,
        desc: TitleComponentDescription,
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined,
        _validFlag: SerializedComponent | undefined,
        outputContext: SerializedComponent
    ): SerializedComponent {
        const { b } = builder;
        const { input } = tables;

        const imageData = makeText(builder, desc.imageURLProperty, input, true, false);
        const image = definedMap(imageData, d => makeImageSource(b, d, desc.imageKind, false));

        const { actions } = this.getActions(desc, tables.input, builder, mutatingScreenKind);
        const { screenContext, lets: screenContextLets } = makeScreenContextForActions(
            builder,
            actions,
            tables,
            mutatingScreenKind
        );
        const onTap = builder.makeSequenceFromActions(
            actions,
            tables,
            screenContext,
            outputContext,
            mutatingScreenKind,
            false
        );

        return b.let(
            screenContextLets,
            b.ui("BigItem", {
                title: makeText(builder, desc.titleProperty, input, true),
                subtitle: makeText(builder, desc.subtitleProperty, input, true),
                image,
                onDropImage: makeDropImageActionForColumnDescription(b, input, desc.imageURLProperty),
                appearance: b.constant(getEnumProperty(desc.appearanceProperty) ?? TitleAppearance.Filled),
                aspect: definedMap(getEnumProperty(desc.aspectProperty), b.constant),
                onTap,
            })
        );
    }

    public getDescriptor(
        desc: TitleComponentDescription | undefined,
        _table: TableGlideType | undefined,
        _ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ComponentDescriptor<TitleComponentDescription> {
        const appearance = definedMap(desc, d => getEnumProperty(d.appearanceProperty));

        const properties: PropertyDescriptor[] = [
            ...makeTitleSubtitleImagePropertyDescriptors(mutatingScreenKind, false),
            makeSummaryImageKindPropertyDescriptor(undefined),
            ...this.getBasePropertyDescriptors(),
            {
                kind: PropertyKind.Enum,
                property: { name: "appearanceProperty" },
                label: "Appearance",
                menuLabel: "Appearance",
                cases: [
                    {
                        value: TitleAppearance.Filled,
                        label: "Full",
                        icon: "buttonFilled",
                    },
                    {
                        value: TitleAppearance.Card,
                        label: "Card",
                        icon: "buttonBordered",
                    },
                    {
                        value: TitleAppearance.Profile,
                        label: "Profile",
                        icon: "buttonFloating",
                    },
                ],
                defaultCaseValue: TitleAppearance.Filled,
                section: PropertySection.Design,
                visual: "small-images",
            },
        ];

        if (appearance !== undefined && appearance !== TitleAppearance.Profile) {
            properties.push({
                kind: PropertyKind.Enum,
                property: { name: "aspectProperty" },
                label: "Size",
                menuLabel: "Size",
                cases: [
                    {
                        // actually means 300px due to back-compat
                        value: undefined,
                        label: "Fixed Height",
                        icon: "borderSquare",
                    },
                    {
                        value: ImageAspectRatio.ThreeByTwo,
                        label: "3:2",
                        icon: "border3by2",
                    },
                    {
                        value: ImageAspectRatio.ThreeByOne,
                        label: "3:1",
                        icon: "border3by1",
                    },
                ],
                defaultCaseValue: undefined,
                section: PropertySection.Design,
                visual: "small-images",
            });
        }

        return {
            name: "Title",
            helpUrl: getDocURL("title"),
            description: "A title, subtitle, and optional image",
            img: "componentTitle",
            group: "Layout",
            properties,
        };
    }

    public getActionDescriptors(
        _desc: TitleComponentDescription | undefined,
        _table: TableGlideType | undefined,
        schema: SchemaInspector | undefined,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): readonly ActionPropertyDescriptor<TitleComponentDescription>[] {
        return [makeDefaultActionDescriptor(schema, mutatingScreenKind, false)];
    }

    public static defaultComponent(desc: TitleDescription): TitleComponentDescription {
        return {
            ...makeEmptyComponentDescription(ComponentKindTitle),
            titleProperty: desc.titleProperty,
            subtitleProperty: desc.subtitleProperty,
            imageURLProperty: desc.imageURLProperty,
            imageKind: desc.imageKind,
            appearanceProperty: desc.appearanceProperty,
            aspectProperty: undefined,
            actions: undefined,
        };
    }
}
